import { Component, EventEmitter, Output, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SheetFormComponent } from '@modules/sheets/components/sheet-form/sheet-form.component';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { Sheet } from '@modules/sheets/types/sheet';
import { StitchType } from '@sharedlib/common/types/StitchType';

/**
 * SBUX-1036: an open workbook, across the list and detail columns. "Pretty much like the 3rd column [the workbook's
 * detail view], but stretched, and the table has the ability to edit it" (the product owner): the detail view's own
 * form, on the editor's page, with the editor (the route's outlet, projected in) as its Sheets tab.
 *
 * Its own component for the reason the header was one: the app state is provided inside the layout's
 * *appSheetsAppContext, which the layout component itself sits outside of.
 */
@Component({
  selector: 'app-sheets-expanded-view',
  standalone: true,
  imports: [SheetFormComponent],
  template: `<app-sheet-form class="view" [sheet]="sheet()" [inEditor]="true" (close)="$event && closed.emit()"><ng-content /></app-sheet-form>`,
  styles: [':host { display: flex; flex-direction: column; height: 100%; min-height: 0; } .view { flex: 1 1 auto; min-height: 0; }'],
})
export class SheetsExpandedViewComponent {
  @Output() closed = new EventEmitter<void>();

  private readonly selected = toSignal(inject(SheetsAppStateService).getMainView(), { initialValue: null });
  /** Until the editor has loaded one (a deep link opens with nothing selected): an empty workbook, nothing to act on. */
  private readonly none = new Sheet();
  private last: Sheet | null = null;

  /**
   * The workbook the editor shows, as the app state has it. A workbook moved to Trash stays: the app state lets go of it
   * (BaseAppStateService clears the main view), but the editor keeps it open, read-only, until it is restored.
   */
  protected readonly sheet = computed(() => {
    const item = this.selected() as { readonly itemType?: StitchType } | null;
    if (item && item.itemType !== StitchType.SHEET_FOLDER) this.last = item instanceof Sheet ? item : new Sheet(item as Partial<Sheet>);
    return this.last ?? this.none;
  });
}
